import React from 'react';
import { motion } from 'framer-motion';
import { Users, Star, MessageSquare } from 'lucide-react';
import { Section } from '../components/ui/Section';
import { ClientLogo } from '../components/ui/ClientLogo';
import { Testimonial } from '../components/ui/Testimonial';

// Create properly typed motion components
const MotionDiv = motion.div;
const MotionH1 = motion.h1;
const MotionH2 = motion.h2;
const MotionP = motion.p;

export default function Clients() {  // Client logos - only 2 clients now
  const clientLogos = [
    { src: '/assets/images/Marquee4.jpg', alt: 'Client 1' },
    { src: '/assets/images/Marquee5.png', alt: 'Client 2' },
  ];

  // Placeholder for testimonials
  const testimonials = [
    {
      text: "Mega Marine Container Terminals has been an exceptional partner for our container needs. Their transparency and attention to detail have saved us both time and resources.",
      author: "Rajesh Kumar",
      company: "SeaTrade Logistics"
    }, 
    {
      text: "The team at Navdeep consistently delivers beyond our expectations. Their strategic yard locations and 24/7 operations have been crucial for our shipping schedules.",
      author: "Sarah Johnson",
      company: "Global Container Lines"
    },
    {
      text: "We've worked with several container yards, but Navdeep stands out for their integrity and quality of service. Their repair services have extended the life of our container fleet significantly.",
      author: "Michael Chen",
      company: "Pacific Shipping"
    },
    {
      text: "The level of care and attention Navdeep gives to every container is remarkable. Their reporting is detailed, their repairs are solid, and their customer service is excellent.",
      author: "Priya Sharma",
      company: "Horizon Logistics"
    },
    {
      text: "As a company that values reliability, we appreciate Navdeep's consistent performance. They've never let us down in five years of partnership.",
      author: "David Wilson",
      company: "Alliance Container Services"
    },
    {
      text: "Navdeep's strategic locations near major ports have significantly reduced our logistics costs and improved our operational efficiency.",
      author: "Anita Patel",
      company: "Meridian Shipping"
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <div className="relative py-24 bg-primary">        <div className="absolute inset-0 overflow-hidden">          <img
            src="/assets/images/Photo1.jpg"
            alt="Global partnerships"
            className="w-full h-full object-cover opacity-20"
            onError={(e) => {
              // Better fallback strategy - try Photo2 first
              if (e.currentTarget.src.includes('Photo1.jpg')) {
                e.currentTarget.src = "/assets/images/Photo2.jpg";
                console.log("Client hero image fallback: trying Photo2");
              } else {
                e.currentTarget.src = "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80";
                console.warn("Client hero image failed, using external fallback");
              }
            }}
            onLoad={() => {
              console.log("Client hero image loaded successfully");
            }}
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center text-white">
            <MotionH1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
            >
              Our Clients
            </MotionH1>
            <MotionP
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl md:text-2xl"
            >
              Trusted by shipping lines and logistics companies worldwide
            </MotionP>
          </div>
        </div>
      </div>      {/* Client Logo Marquee */}
      <Section
        title="Our Trusted Partners"
        subtitle="Proud to serve leading companies in the shipping and logistics industry"
        className="py-20 bg-gradient-to-br from-slate-50 via-white to-slate-50 border-y border-slate-200"
        withBackground={false}
      >
        <div className="mb-8 text-center">
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Our commitment to excellence has earned us the trust of industry leaders across the globe
          </p>
        </div>        <ClientLogo logos={clientLogos} />
        
        <div className="mt-16 grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {clientLogos.slice(0, 2).map((logo, index) => (
            <MotionDiv
              key={`featured-${index}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white dark:bg-gray-800 shadow-lg rounded-xl overflow-hidden border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
            >
              <div className="p-6 flex flex-col items-center">
                <div className="w-full h-32 flex items-center justify-center mb-4">
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    className="max-h-24 max-w-full object-contain"
                    onError={(e) => {
                      e.currentTarget.src = "/assets/images/mainwebsiteLogo.png";
                      e.currentTarget.style.objectFit = "contain";
                    }}
                  />
                </div>
                {/* <h3 className="text-xl font-bold text-center">{logo.alt}</h3> */}
              </div>
            </MotionDiv>
          ))}
        </div>
      </Section>

      {/* Client Statistics */}
      <Section className="bg-gray-50 dark:bg-navy">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-white dark:bg-navy-light p-8 rounded-lg shadow-sm"
          >
            <Users className="h-12 w-12 mx-auto mb-4 text-primary" />
            <h3 className="text-4xl font-bold mb-2 text-primary">100+</h3>
            <p className="text-gray-700 dark:text-gray-300 font-medium">Active Clients</p>
          </MotionDiv>
          
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white dark:bg-navy-light p-8 rounded-lg shadow-sm"
          >
            <Star className="h-12 w-12 mx-auto mb-4 text-primary" />
            <h3 className="text-4xl font-bold mb-2 text-primary">98%</h3>
            <p className="text-gray-700 dark:text-gray-300 font-medium">Client Satisfaction</p>
          </MotionDiv>
          
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white dark:bg-navy-light p-8 rounded-lg shadow-sm"
          >
            <MessageSquare className="h-12 w-12 mx-auto mb-4 text-primary" />
            <h3 className="text-4xl font-bold mb-2 text-primary">15+</h3>
            <p className="text-gray-700 dark:text-gray-300 font-medium">Years of Trust</p>
          </MotionDiv>
        </div>
      </Section>

      {/* Client Industries */}
      <Section
        title="Industries We Serve"
        subtitle="Our expertise spans across various sectors in the maritime and logistics industry"
      >
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {["Shipping Lines", "Freight Forwarders", "Container Leasing", "Import/Export", "Manufacturing", "Retail Logistics", "Project Cargo", "Agricultural Exports"].map((industry, index) => (
            <MotionDiv
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className="bg-white dark:bg-navy-light p-5 rounded-lg shadow-sm border border-gray-100 dark:border-navy text-center"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{industry}</h3>
            </MotionDiv>
          ))}
        </div>
      </Section>      {/* Testimonials */}
      <Section
        title="What Our Clients Say"
        subtitle="Don't just take our word for it—hear from the businesses that trust us with their containers"
        className="bg-gray-50 dark:bg-navy"
      >
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
          {testimonials.map((testimonial, index) => (
            <Testimonial
              key={index}
              text={testimonial.text}
              author={testimonial.author}
              company={testimonial.company}
            />
          ))}
        </div>
      </Section>

      {/* Become a Client */}
      <Section className="bg-primary text-white">
        <div className="max-w-3xl mx-auto text-center">
          <MotionH2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-3xl md:text-4xl font-bold mb-6"
          >
            Ready to Experience Our Services?
          </MotionH2>
          <MotionP
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-xl mb-8 text-gray-200"
          >
            Join our growing list of satisfied clients and discover why leading companies trust Mega Marine Container Terminals for their container needs.
          </MotionP>
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <a 
              href="/contact" 
              className="inline-flex items-center justify-center bg-white text-primary hover:bg-gray-100 px-8 py-3 rounded-md font-medium transition-colors duration-300"
            >
              Get In Touch
            </a>
          </MotionDiv>
        </div>
      </Section>
    </div>
  );
}


